// client/src/index.js
// 전체 수정된 버전

import "./sentry";  // Sentry 초기화 — 반드시 첫 import
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { warmApply, readWarm } from './utils/warmApplyCache';
import { warmPending, readPendingWarm } from './utils/warmPendingCache';
import { warmMarketerProjects } from './utils/marketerProjectCache';
import { warmSelectedProjects } from './utils/influencerContractCache';
import { warmResultPendingProjects } from './utils/influencerResultCache';

const root = ReactDOM.createRoot(document.getElementById('root'));

window.warmupInfluencer = async function() {
  try {
    const uid = window.__ME__?.id;
    if (!uid) return;

    // ✅ 병렬 warmup 시작 (선정된 프로젝트 추가)
    const warmups = Promise.all([
      warmApply({ uid, sort: 'deadline', page: 1, awaitReady: 'all' }),
      warmPending({ uid, awaitReady: 'all' }),
      warmSelectedProjects(uid)
    ]);

    // ✅ 즉시 preload 주입 (50ms 후)
    setTimeout(() => {
      injectPreloadFromWarm(uid, 'deadline', 1);
      injectPreloadFromPendingWarm(uid);
    }, 50);

    // ✅ warmup 완료 대기
    await warmups;

    // ✅ 백그라운드 추가 로드 (non-blocking)
    warmApply({ uid, sort: 'created', page: 1, awaitReady: 'covers-decode' });
    injectPreloadFromWarm(uid, 'created', 1);
    
    // ✅ 결과물 제출 대상 warmup (백그라운드)
    warmResultPendingProjects(uid).catch(()=>{});

    // 정기 갱신
    if (!window.__INF_WARM_TIMER) {
      window.__INF_WARM_TIMER = setInterval(() => {
        warmApply({ uid, sort: 'deadline', page: 1, awaitReady: 'all' }).catch(()=>{});
        warmPending({ uid, awaitReady: 'covers' }).catch(()=>{});
        warmSelectedProjects(uid).catch(()=>{});
        warmResultPendingProjects(uid).catch(()=>{}); // ✅ 추가
      }, 60 * 1000);
    }
  } catch {}
};

// ✅ 광고주 warmup 함수
window.warmupMarketer = async function() {
  try {
    const uid = window.__ME__?.id;
    if (!uid) return;

    // 프로젝트 목록 + 첫 번째 프로젝트 지원자 목록 로드
    await warmMarketerProjects(uid);
    
    // ✅ warmup 완료 플래그 설정
    window.__MARKETER_WARM_READY__ = true;
  } catch {}
};

function injectPreloadFromWarm(uid, sort='deadline', page=1) {
  try {
    const w = readWarm(uid, sort, page, 15);
    const covers = Array.isArray(w?.items) ? w.items.map(p => p?.cover).filter(Boolean).slice(0, 15) : [];
    const head = document.head;
    const added = new Set();
    covers.forEach(href => {
      if (!href || added.has(href) || head.querySelector(`link[rel="preload"][href="${href}"]`)) return;
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = href;
      head.appendChild(link);
      added.add(href);
    });
  } catch {}
}

// ✅ 추가: 지원 중인 프로젝트 preload
function injectPreloadFromPendingWarm(uid) {
  try {
    const warm = readPendingWarm(uid);
    if (!warm?.items) return;
    
    const covers = warm.items
      .map(app => app?.project?.cover)
      .filter(Boolean)
      .slice(0, 10);
    
    const head = document.head;
    const added = new Set();
    
    covers.forEach(href => {
      if (!href || added.has(href) || head.querySelector(`link[rel="preload"][href="${href}"]`)) return;
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = href;
      head.appendChild(link);
      added.add(href);
    });
  } catch {}
}

// ✅ 전역 이벤트 리스너 배열 초기화 (렌더링 전!)
window.__USER_READY_LISTENERS__ = [];

// ✅ 글로벌 이벤트 리스너 등록 (한 번만!)
window.addEventListener('user-ready', (event) => {
  if (window.__USER_READY_LISTENERS__) {
    window.__USER_READY_LISTENERS__.forEach(fn => {
      try {
        fn(event);
      } catch (e) {
        console.error('user-ready 리스너 실행 오류:', e);
      }
    });
  }
});

// ✅✅✅ 1단계: 즉시 렌더링 (동기적으로!)
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// ✅✅✅ 2단계: 사용자 정보 로드 (비동기, 별도 실행)
(async function loadUserInfo() {
  try {
    const res = await fetch('/api/user/me', { credentials: 'include' });
    const me = await res.json().catch(() => ({}));
    
    if (res.ok && me?.ok) {
      const uid = me?.user?.id || me?.id;
      
      if (me.role === 'influencer' && uid) {
        window.__ME__ = { id: uid, role: 'influencer' };
      } else if (me.role === 'marketer' && uid) {
        window.__ME__ = { id: uid, role: 'marketer' };
      }
      
      // ✅ window.__ME__ 설정 완료 이벤트 발송
      if (window.__ME__) {
        window.dispatchEvent(new CustomEvent('user-ready', { 
          detail: window.__ME__ 
        }));
        
        // ✅ warmup 실행
        if (me.role === 'influencer' && window.warmupInfluencer) {
          window.warmupInfluencer();
        } else if (me.role === 'marketer' && window.warmupMarketer) {
          window.warmupMarketer();
        }
      }
    }
  } catch (err) {
    console.error('사용자 정보 조회 실패:', err);
  }
})();

// 사용자 상호작용 시 프리패치 일시정지
(function installInteractionPause(){
  if (typeof window === 'undefined') return;
  if (window.__PAUSE_PREFETCH_INSTALLED__) return;
  window.__PAUSE_PREFETCH_INSTALLED__ = true;
  const pause = () => { try { window.__PAUSE_PREFETCH_UNTIL = Date.now() + 1200; } catch {} }; 
  window.addEventListener('pointerdown', pause, { passive: true, capture: true });
  window.addEventListener('keydown', pause, { passive: true, capture: true });
})();

reportWebVitals();